import AddOutlined from "@mui/icons-material/AddOutlined";
import DeleteOutline from "@mui/icons-material/DeleteOutline";
import EditOutlined from "@mui/icons-material/EditOutlined";
import KeyOutlined from "@mui/icons-material/KeyOutlined";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import TuneOutlined from "@mui/icons-material/TuneOutlined";
import BusinessOutlined from "@mui/icons-material/BusinessOutlined";
import HistoryOutlined from "@mui/icons-material/HistoryOutlined";
import SendOutlined from "@mui/icons-material/SendOutlined";
import {
  Avatar,
  Box,
  Button,
  Chip,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormGroup,
  IconButton,
  InputAdornment,
  InputLabel,
  LinearProgress,
  Menu,
  MenuItem,
  Pagination,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import axios from "axios";
import { User } from "../../utils";
import { useEffect, useState, useCallback } from "react";
import { debounce } from "lodash";
import LoadingButton from "../../components/LoadingButton";
import { Company } from "../../types";
import { hostname, parseError, addImageDimensions } from "../../utils";
import { useEmployeeTerminology } from "../../utils/terminology";
import SsoWizard from "../settings/SsoWizard";
import ManageFeaturesDialog from "./dialogs/ManageFeaturesDialog";
import AuditLogDialog from "./dialogs/AuditLogDialog";
import { SELECTABLE_FEATURES, FEATURE_STATES } from "../../constants/features";
import DepartmentSelector from "../../components/DepartmentSelector";
import Departments from "./Departments";

export default ({
  company,
  companyId,
  getToken,
  currentUser,
  eventListener,
  openDialog,
  userRole,
}: {
  company: Company;
  companyId: string;
  getToken: () => Promise<string>;
  currentUser: any | User;
  eventListener: (event: any) => void;
  openDialog: (title: string, content: string, buttonText: string, onClick: () => void) => void;
  userRole?: string | null;
}) => {
  const { employees: employeesText, employeesLower } = useEmployeeTerminology();
  const [init, setInit] = useState(false);
  const [loading, setLoading] = useState(true);
  const [companyUsers, setCompanyUsers] = useState<any[]>([]);
  const [pagination, setPagination] = useState({
    page: 1,
    per_page: 10,
    total: 0,
    pages: 0,
    has_next: false,
    has_prev: false
  });
  const [searchQuery, setSearchQuery] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [modalLoading, setModalLoading] = useState(false);
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("member");
  const [ssoWizardOpen, setSsoWizardOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedUser, setSelectedUser] = useState<any>(null);
  const [editRoleOpen, setEditRoleOpen] = useState(false);
  const [editRole, setEditRole] = useState("member");
  const [manageFeaturesOpen, setManageFeaturesOpen] = useState(false);
  const [selectedFeatures, setSelectedFeatures] = useState<string[]>([]);
  const [companyAvailableFeatures, setCompanyAvailableFeatures] = useState<string[]>(SELECTABLE_FEATURES);
  const [manageDepartmentsOpen, setManageDepartmentsOpen] = useState(false);
  const [selectedDepartmentIds, setSelectedDepartmentIds] = useState<string[]>([]);
  const [departmentsDialogOpen, setDepartmentsDialogOpen] = useState(false);
  const [auditLogOpen, setAuditLogOpen] = useState(false);
  const [resendingInvite, setResendingInvite] = useState(false);

  const getData = async (page: number = 1, search: string = '') => {
    const currentToken = await getToken();
    setLoading(true);
    const params = new URLSearchParams({
      page: page.toString(),
      per_page: '10',
      ...(search && { search })
    });

    await axios
      .get(`${hostname}/company_users/${companyId}?${params}`, {
        headers: {
          Authorization: `Bearer ${currentToken}`,
        },
      })
      .then((res) => {
        setCompanyUsers(res.data.company_users);
        setPagination(res.data.pagination);
      })
      .catch((err: any) => {
        eventListener({
          type: "SET_ERROR",
          error: parseError(err),
        });
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const addCompanyUser = async () => {
    if (!email || !role) return;
    const currentToken = await getToken();
    setModalLoading(true);

    // Only include allowedFeatures for member role
    const payload: any = {
      companyId: companyId,
      email: email,
      role: role,
    };

    if (role === "member" && selectedFeatures.length > 0 && selectedFeatures.length < SELECTABLE_FEATURES.length) {
      payload.allowedFeatures = selectedFeatures;
    }

    await axios
      .post(
        `${hostname}/company_users`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${currentToken}`,
          },
        }
      )
      .catch((err: any) => {
        eventListener({
          type: "SET_ERROR",
          error: parseError(err),
        });
      })
      .finally(() => {
        setModalOpen(false);
        setModalLoading(false);
        setEmail("");
        setRole("member");
        setSelectedFeatures([]);
        getData(pagination.page, searchQuery);
      });
  };

  const deleteCompanyUser = async (id: string, email: string) => {
    const currentToken = await getToken();
    openDialog(
      "Remove Member",
      `Are you sure you want to remove ${email}?`,
      "Confirm",
      () => {
        axios
          .delete(`${hostname}/company_users/${id}`, {
            headers: {
              Authorization: `Bearer ${currentToken}`,
            },
          })
          .catch((err) => {
            eventListener({
              type: "SET_ERROR",
              error: parseError(err),
            });
          })
          .finally(() => {
            getData(pagination.page, searchQuery);
          });
      }
    );
  };

  const updateCompanyUserRole = async () => {
    // Store selectedUser reference before it might get cleared
    const userToUpdate = selectedUser;

    if (!userToUpdate || !editRole) {
      console.log('Missing selectedUser or editRole:', { selectedUser, userToUpdate, editRole });
      return;
    }

    console.log('Updating role for user:', userToUpdate.id, 'to role:', editRole);

    const currentToken = await getToken();
    setModalLoading(true);

    try {
      const response = await axios.put(
        `${hostname}/company_users/${userToUpdate.id}`,
        {
          role: editRole,
        },
        {
          headers: {
            Authorization: `Bearer ${currentToken}`,
          },
        }
      );

      console.log('Role update successful:', response.data);

      eventListener({
        type: "SET_INFO",
        info: "Role updated successfully",
      });
    } catch (err: any) {
      console.error('Role update failed:', err);
      eventListener({
        type: "SET_ERROR",
        error: parseError(err),
      });
    } finally {
      setEditRoleOpen(false);
      setModalLoading(false);
      setSelectedUser(null);
      getData(pagination.page, searchQuery);
    }
  };

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>, user: any) => {
    setAnchorEl(event.currentTarget);
    setSelectedUser(user);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedUser(null);
  };

  const handleEditRole = () => {
    setEditRole(selectedUser.role);
    setEditRoleOpen(true);
    // Only close the menu, but don't clear selectedUser yet
    setAnchorEl(null);
  };

  const handleDelete = () => {
    deleteCompanyUser(selectedUser.id, selectedUser.email);
    handleMenuClose();
  };

  const handleManageFeatures = () => {
    setManageFeaturesOpen(true);
    setAnchorEl(null);
  };

  const handleManageDepartments = async () => {
    if (selectedUser) {
      // Load user's departments
      const currentToken = await getToken();
      try {
        const response = await axios.get(
          `${hostname}/company-users/${selectedUser.id}/departments?companyId=${companyId}`,
          {
            headers: {
              Authorization: `Bearer ${currentToken}`,
            },
          }
        );
        setSelectedDepartmentIds(response.data.map((dept: any) => dept.id));
        setManageDepartmentsOpen(true);
      } catch (err: any) {
        eventListener({
          type: "SET_ERROR",
          error: parseError(err),
        });
      }
    }
    setAnchorEl(null);
  };

  const handleResendInvite = async () => {
    if (!selectedUser) return;

    const currentToken = await getToken();
    setResendingInvite(true);
    setAnchorEl(null);

    try {
      await axios.post(
        `${hostname}/company_users/${selectedUser.id}/resend-invite`,
        {},
        {
          headers: {
            Authorization: `Bearer ${currentToken}`,
          },
        }
      );

      eventListener({
        type: "SET_INFO",
        info: `Invitation resent to ${selectedUser.email}`,
      });
    } catch (err: any) {
      eventListener({
        type: "SET_ERROR",
        error: parseError(err),
      });
    } finally {
      setResendingInvite(false);
      setSelectedUser(null);
    }
  };

  const saveDepartments = async () => {
    if (!selectedUser) return;

    const currentToken = await getToken();
    setModalLoading(true);

    try {
      await axios.put(
        `${hostname}/company-users/${selectedUser.id}/departments`,
        {
          companyId,
          departmentIds: selectedDepartmentIds,
        },
        {
          headers: {
            Authorization: `Bearer ${currentToken}`,
          },
        }
      );

      eventListener({
        type: "SET_INFO",
        info: "Departments updated successfully",
      });

      setManageDepartmentsOpen(false);
      setSelectedDepartmentIds([]);
      getData(pagination.page, searchQuery);
    } catch (err: any) {
      eventListener({
        type: "SET_ERROR",
        error: parseError(err),
      });
    } finally {
      setModalLoading(false);
    }
  };

  const handleSaveFeatures = async (features: string[]) => {
    // If we're editing an existing user
    if (selectedUser) {
      const currentToken = await getToken();
      setModalLoading(true);

      try {
        await axios.put(
          `${hostname}/company_users/${selectedUser.id}/features`,
          {
            allowedFeatures: features,
          },
          {
            headers: {
              Authorization: `Bearer ${currentToken}`,
            },
          }
        );

        eventListener({
          type: "SET_INFO",
          info: "Feature access updated successfully",
        });

        // Refresh the list
        getData(pagination.page, searchQuery);
      } catch (err: any) {
        eventListener({
          type: "SET_ERROR",
          error: parseError(err),
        });
      } finally {
        setModalLoading(false);
        setManageFeaturesOpen(false);
      }
    } else {
      // If we're in the add member dialog
      setSelectedFeatures(features);
      setManageFeaturesOpen(false);
    }
  };

  const getRoleChipColor = (role: string) => {
    switch (role) {
      case 'owner':
        return 'primary';
      case 'admin':
        return 'warning';
      case 'member':
        return 'default';
      default:
        return 'default';
    }
  };

  const getAvatarText = (user: any) => {
    if (user.displayName) {
      // Get initials from display name
      const names = user.displayName.trim().split(' ');
      if (names.length >= 2) {
        return names[0][0] + names[names.length - 1][0];
      }
      return names[0][0];
    }
    // Fallback to first letter of email
    return user.email ? user.email[0].toUpperCase() : '?';
  };

  const getUserDisplayName = (user: any) => {
    return user.displayName || user.email;
  };

  // Debounced search function
  const debouncedSearch = useCallback(
    debounce((query: string) => {
      setPagination(prev => ({ ...prev, page: 1 })); // Reset to first page when searching
      getData(1, query);
    }, 300),
    []
  );

  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    const query = event.target.value;
    setSearchQuery(query);
    debouncedSearch(query);
  };

  const handleClearSearch = () => {
    setSearchQuery('');
    setPagination(prev => ({ ...prev, page: 1 }));
    getData(1, '');
  };

  const handlePageChange = (event: React.ChangeEvent<unknown>, value: number) => {
    setPagination(prev => ({ ...prev, page: value }));
    getData(value, searchQuery);
  };

  const fetchCompanyFeatures = async () => {
    try {
      const currentToken = await getToken();

      // Fetch usage data to get the company's feature access
      const response = await axios.get(
        `${hostname}/usage?companyId=${companyId}`,
        {
          headers: {
            Authorization: `Bearer ${currentToken}`,
          },
        }
      );

      if (response.data.features) {
        // Filter to only include features that are enabled (not locked or hidden)
        const enabledFeatures = Object.keys(response.data.features).filter(
          (feature) => response.data.features[feature] === FEATURE_STATES.ENABLED
        );

        // Further filter to only include selectable features (exclude system features)
        const enabledSelectableFeatures = enabledFeatures.filter((feature) =>
          SELECTABLE_FEATURES.includes(feature as any)
        );

        // Reorder to match SELECTABLE_FEATURES order
        const availableFeatures = SELECTABLE_FEATURES.filter((feature) =>
          enabledSelectableFeatures.includes(feature)
        );

        setCompanyAvailableFeatures(availableFeatures);
      }
    } catch (err) {
      console.error("Failed to fetch company features:", err);
      // Fall back to all selectable features on error
      setCompanyAvailableFeatures(SELECTABLE_FEATURES);
    }
  };

  useEffect(() => {
    if (currentUser.uid) {
      getData();
      fetchCompanyFeatures();
    }
  }, [currentUser.uid, companyId]);

  const isOwner = () => {
    return userRole === "owner";
  };

  const canAddMembers = () => {
    return userRole === "owner" || userRole === "admin";
  };

  // Don't return early on loading - we want to show the search interface

  return (
    <Box style={{ marginTop: 20 }}>
      {/* Search Bar */}
      <Box sx={{ mb: 2 }}>
        <TextField
          fullWidth
          variant="outlined"
          placeholder="Search collaborators by email..."
          value={searchQuery}
          onChange={handleSearch}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
            endAdornment: searchQuery && (
              <InputAdornment position="end">
                <IconButton onClick={handleClearSearch} edge="end">
                  <ClearIcon />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
      </Box>

      {/* Action Buttons */}
      {userRole && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            gap: 2,
            alignItems: { xs: 'stretch', sm: 'flex-start' },
            mb: 2
          }}
        >
          {canAddMembers() && (
            <Button
              data-tutorial="invite-collaborator"
              variant="contained"
              onClick={() => setModalOpen(true)}
              startIcon={<AddOutlined />}
              sx={{ minWidth: { xs: 'auto', sm: 'auto' } }}
            >
              Add Member
            </Button>
          )}
          {isOwner() && (
            <Button
              data-tutorial="role-settings"
              variant="outlined"
              onClick={() => setSsoWizardOpen(true)}
              startIcon={<KeyOutlined />}
              sx={{ minWidth: { xs: 'auto', sm: 'auto' } }}
            >
              Configure SSO
            </Button>
          )}
          {(userRole === 'admin' || userRole === 'owner') && (
            <Button
              variant="outlined"
              onClick={() => setDepartmentsDialogOpen(true)}
              startIcon={<BusinessOutlined />}
              sx={{ minWidth: { xs: 'auto', sm: 'auto' } }}
            >
              Manage Departments
            </Button>
          )}
          {(userRole === 'admin' || userRole === 'owner') && (
            <Button
              variant="outlined"
              onClick={() => setAuditLogOpen(true)}
              startIcon={<HistoryOutlined />}
              sx={{ minWidth: { xs: 'auto', sm: 'auto' } }}
            >
              Audit Log
            </Button>
          )}
        </Box>
      )}
      <TableContainer data-tutorial="collaborator-list" component={Paper} variant="outlined">
        {loading && <LinearProgress />}
        <Table sx={{ minWidth: 650 }} aria-label="simple table">
          <TableHead>
            <TableRow>
              <TableCell>User</TableCell>
              <TableCell>Role</TableCell>
              <TableCell>Departments</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && companyUsers.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} sx={{ textAlign: 'center', py: 4 }}>
                  <Typography variant="body2" color="text.secondary">
                    Loading collaborators...
                  </Typography>
                </TableCell>
              </TableRow>
            ) : companyUsers.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} sx={{ textAlign: 'center', py: 4 }}>
                  <Typography variant="body2" color="text.secondary">
                    {searchQuery ? `No collaborators found matching "${searchQuery}"` : 'No collaborators found'}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              companyUsers.map((row) => (
                <TableRow
                  key={row.email}
                  sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
                >
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Avatar
                        src={addImageDimensions(row.photoURL, 80, 80)}
                        alt={getUserDisplayName(row)}
                        sx={{ width: 40, height: 40 }}
                      >
                        {getAvatarText(row)}
                      </Avatar>
                      <Box>
                        <Typography variant="body2" fontWeight="medium">
                          {getUserDisplayName(row)}
                        </Typography>
                        {row.displayName && (
                          <Typography variant="caption" color="text.secondary">
                            {row.email}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', flexWrap: 'wrap' }}>
                      <Chip
                        label={row.role.substring(0, 1).toUpperCase() + row.role.substring(1)}
                        color={getRoleChipColor(row.role)}
                        size="small"
                      />
                      {row.uid === company.uid && (
                        <Tooltip
                          title={
                            <Box>
                              <Typography variant="body2" sx={{ mb: 1 }}>
                                The Billing Owner is the main owner responsible for subscription billing and company credits.
                              </Typography>
                              <Typography variant="body2">
                                To transfer Billing Owner status, use the "Transfer Ownership" option in the{" "}
                                <a
                                  href="/dashboard/companies"
                                  style={{ color: 'inherit', textDecoration: 'underline' }}
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  Companies list
                                </a>{" "}
                                menu.
                              </Typography>
                            </Box>
                          }
                          arrow
                          placement="top"
                        >
                          <Chip
                            label="Billing Owner"
                            size="small"
                            color="primary"
                            variant="outlined"
                          />
                        </Tooltip>
                      )}
                      {!row.hasLoggedIn && (
                        <Chip
                          label="Pending"
                          size="small"
                          variant="outlined"
                          sx={{ color: 'text.secondary', borderColor: 'text.secondary' }}
                        />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    {row.role === 'member' && row.departments && row.departments.length > 0 ? (
                      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                        {row.departments.map((dept: any) => (
                          <Chip
                            key={dept.id}
                            label={dept.name}
                            size="small"
                            sx={{
                              backgroundColor: dept.color || undefined,
                              color: dept.color ? '#fff' : undefined,
                            }}
                          />
                        ))}
                      </Box>
                    ) : row.role === 'member' ? (
                      <Typography variant="caption" color="text.secondary">
                        All departments
                      </Typography>
                    ) : (
                      <Typography variant="caption" color="text.secondary">
                        All departments
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell align="right">
                    {isOwner() && (
                      <IconButton
                        size="small"
                        onClick={(e) => handleMenuClick(e, row)}
                      >
                        <MoreVertIcon fontSize="small" />
                      </IconButton>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      {pagination.pages > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
          <Pagination
            count={pagination.pages}
            page={pagination.page}
            onChange={handlePageChange}
            color="primary"
            showFirstButton
            showLastButton
          />
        </Box>
      )}

      {/* Results Summary */}
      <Box sx={{ mt: 2, textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          Showing {companyUsers.length} of {pagination.total} collaborators
          {searchQuery && ` matching "${searchQuery}"`}
        </Typography>
      </Box>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={handleEditRole} disabled={selectedUser?.role === 'owner'}>
          <EditOutlined fontSize="small" sx={{ mr: 1 }} />
          Edit Role
        </MenuItem>
        <MenuItem
          onClick={handleManageFeatures}
          disabled={selectedUser?.role === 'owner' || selectedUser?.role === 'admin'}
        >
          <TuneOutlined fontSize="small" sx={{ mr: 1 }} />
          Manage Features
        </MenuItem>
        <MenuItem
          onClick={handleManageDepartments}
          disabled={selectedUser?.role === 'owner' || selectedUser?.role === 'admin'}
        >
          <BusinessOutlined fontSize="small" sx={{ mr: 1 }} />
          Manage Departments
        </MenuItem>
        {selectedUser && !selectedUser.hasLoggedIn && (
          <MenuItem
            onClick={handleResendInvite}
            disabled={resendingInvite}
          >
            <SendOutlined fontSize="small" sx={{ mr: 1 }} />
            Resend Invite
          </MenuItem>
        )}
        <MenuItem onClick={handleDelete} disabled={selectedUser?.uid === currentUser?.uid || selectedUser?.email?.toLowerCase() === currentUser?.email?.toLowerCase()}>
          <DeleteOutline fontSize="small" sx={{ mr: 1 }} />
          Remove
        </MenuItem>
      </Menu>

      <Dialog
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Add Member
          <IconButton
            aria-label="close"
            onClick={() => setModalOpen(false)}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 3 }}>
            Adding member gives them access to your company's AI {employeesText}
          </Typography>
          <TextField
            fullWidth
            type="email"
            value={email}
            label="Email"
            onChange={(e: any) => setEmail(e.target.value)}
            sx={{ mb: 2 }}
            autoFocus
          />
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Role</InputLabel>
            <Select
              label="Role"
              value={role}
              onChange={(e: any) => {
                setRole(e.target.value);
                // Reset features when changing role
                if (e.target.value !== "member") {
                  setSelectedFeatures([]);
                }
              }}
            >
              <MenuItem value="member">Member</MenuItem>
              <MenuItem value="admin">Admin</MenuItem>
              <MenuItem value="owner">Owner</MenuItem>
            </Select>
          </FormControl>

          {role === "member" && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Feature Access (Optional)
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
                By default, members have access to all features. You can restrict access by selecting specific features.
              </Typography>
              <Button
                variant="outlined"
                size="small"
                startIcon={<TuneOutlined />}
                onClick={() => {
                  // Initialize with all features if none selected
                  if (selectedFeatures.length === 0) {
                    setSelectedFeatures(SELECTABLE_FEATURES);
                  }
                  setManageFeaturesOpen(true);
                }}
              >
                {selectedFeatures.length > 0
                  ? `${selectedFeatures.length} features selected`
                  : 'Select Features'}
              </Button>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setModalOpen(false)}>Cancel</Button>
          <LoadingButton
            variant="contained"
            onClick={() => addCompanyUser()}
            loading={modalLoading}
            disabled={!email || !role}
          >
            Add Member
          </LoadingButton>
        </DialogActions>
      </Dialog>

      <Dialog
        open={editRoleOpen}
        onClose={() => setEditRoleOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Edit Role
          <IconButton
            aria-label="close"
            onClick={() => setEditRoleOpen(false)}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 3 }}>
            Change role for {selectedUser?.email}
          </Typography>
          <FormControl fullWidth>
            <InputLabel>Role</InputLabel>
            <Select
              label="Role"
              value={editRole}
              onChange={(e: any) => setEditRole(e.target.value)}
            >
              <MenuItem value="member">Member</MenuItem>
              <MenuItem value="admin">Admin</MenuItem>
              {selectedUser?.role !== 'owner' && (
                <MenuItem value="owner">Owner</MenuItem>
              )}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditRoleOpen(false)}>Cancel</Button>
          <LoadingButton
            variant="contained"
            onClick={() => {
              console.log('Update Role button clicked', { editRole, selectedUserRole: selectedUser?.role, disabled: editRole === selectedUser?.role });
              updateCompanyUserRole();
            }}
            loading={modalLoading}
            disabled={editRole === selectedUser?.role}
          >
            Update Role
          </LoadingButton>
        </DialogActions>
      </Dialog>
      <SsoWizard
        open={ssoWizardOpen}
        onClose={() => setSsoWizardOpen(false)}
        getToken={getToken}
        companyId={companyId}
        eventListener={eventListener}
      />
      <ManageFeaturesDialog
        open={manageFeaturesOpen}
        onClose={() => {
          setManageFeaturesOpen(false);
          // Clear selectedUser only if we're not in add member flow
          if (!modalOpen) {
            setSelectedUser(null);
          }
        }}
        user={selectedUser || { email, allowedFeatures: selectedFeatures }}
        onSave={handleSaveFeatures}
        availableFeatures={companyAvailableFeatures}
      />

      <Dialog
        open={manageDepartmentsOpen}
        onClose={() => {
          setManageDepartmentsOpen(false);
          setSelectedDepartmentIds([]);
          if (!modalOpen) {
            setSelectedUser(null);
          }
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Manage Departments
          <IconButton
            aria-label="close"
            onClick={() => {
              setManageDepartmentsOpen(false);
              setSelectedDepartmentIds([]);
            }}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Box mt={1}>
            <Typography variant="body2" color="text.secondary" mb={2}>
              Assign {selectedUser?.email || 'this member'} to specific departments.
              If no departments are assigned, they can see all {employeesLower} (backwards compatible).
            </Typography>
            <DepartmentSelector
              companyId={companyId}
              getToken={getToken}
              selectedDepartmentIds={selectedDepartmentIds}
              onChange={setSelectedDepartmentIds}
              eventListener={eventListener}
              label="Departments"
              helperText={`Leave empty to allow access to all ${employeesLower}`}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <LoadingButton
            onClick={() => {
              setManageDepartmentsOpen(false);
              setSelectedDepartmentIds([]);
            }}
            loading={modalLoading}
            disabled={modalLoading}
            variant="text"
          >
            Cancel
          </LoadingButton>
          <LoadingButton
            onClick={saveDepartments}
            loading={modalLoading}
            variant="contained"
          >
            Save
          </LoadingButton>
        </DialogActions>
      </Dialog>

      <Dialog
        open={departmentsDialogOpen}
        onClose={() => setDepartmentsDialogOpen(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          Manage Departments
          <IconButton
            aria-label="close"
            onClick={() => setDepartmentsDialogOpen(false)}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Departments
            company={company}
            companyId={companyId}
            getToken={getToken}
            currentUser={currentUser}
            eventListener={eventListener}
            openDialog={openDialog}
            userRole={userRole}
          />
        </DialogContent>
      </Dialog>

      <AuditLogDialog
        open={auditLogOpen}
        onClose={() => setAuditLogOpen(false)}
        companyId={companyId}
        getToken={getToken}
        eventListener={eventListener}
      />
    </Box>
  );
};
